/* eslint-disable */
// @ts-nocheck
import * as R from 'ramda';
import B from 'bluebird';
import dash_api from '../../main/support/dash_api';
import { generateConstants, removeQueryParam, generateDashDomain, ReduxAsyncAction } from '../support/utils';
import type { ShareableActionPayload } from '../types/shareableTypes';
import NotificationActions from '../../main/actions/NotificationActions';

/**
 * per JOUR-3119, we are now segmenting sharable state by user then app id, which would look like this:
 * {
 *   BRANCH_SHAREABLE : {
 *     [userId]: {
 *       [appId] : {
 *        ...sharable data...
 *       }
 *     }
 * }};
 * For setting state, if there is localStorage set at the old location, we'll move it to the new location.
 * For getting state, we look to new location first and fall back to old location.
 * This should all be transparent to the consumers of shareable data
 */
// EG-2270: We are now supporting organization-level shareable state
// The id under which the data will be stored (referred to as entityId throughout this file) can be an appId or an organizationId
// The structure will be the same as above, where entityId is a child property of userId within BRANCH_SHAREABLE
export const getAppId = R.path(['App', 'context', 'view', 'app_id']);
export const getOrganizationId = R.path(['App', 'context', 'view', 'organization_id']);
const userId = R.path(['App', 'context', 'userId'], window);
export const BRANCH_SHAREABLE = 'BRANCH_SHAREABLE';
const BOOKMARK_PATH = '/v1/bookmark';

export function getLocalStorage(): {} {
  const item = localStorage.getItem(BRANCH_SHAREABLE);

  if (!item) {
    return {};
  }

  try {
    return JSON.parse(item) || {};
  } catch (err) {
    return {};
  }
}

export function setLocalStorage(namespace: string, data: any) {
  const appId = getAppId(window);
  const organizationId = getOrganizationId(window);
  const entityId = appId || organizationId;
  const allData = getLocalStorage();

  if (R.path([entityId, namespace], allData)) {
    if (!allData[userId]) {
      allData[userId] = {};
    }

    allData[userId] = { ...allData[userId], [entityId]: allData[entityId] };
    delete allData[entityId];
  }

  const state = R.assocPath([userId, entityId, namespace], data, allData);
  try {
    localStorage.setItem(BRANCH_SHAREABLE, JSON.stringify(state));
  } catch (e) {
    console.error(e);
    NotificationActions.showNotification(
      'The data size for the selected timeframe exceeds the sharing limit. Please reduce the timeframe or the amount of data you wish to share and try again.',
      'error'
    );
  }
}

export const SHAREABLE = generateConstants('SHAREABLE', [
  'mount',
  'from_link',
  'set',
  'reset',
  'link_request',
  'link_response',
  'link_error',
]);

export function mountFromLocalStorage(): {
  type: string;
  data: Record<string, any>;
} {
  const localStorage = getLocalStorage();
  const appId = getAppId(window);
  const orgId = getOrganizationId(window);
  const entityId = appId || orgId;
  const deprecatedLocation = R.path([entityId], localStorage);
  const newLocation = R.path([userId, entityId], localStorage);
  return {
    type: SHAREABLE.mount,
    data: newLocation || deprecatedLocation || {},
  };
}

export function mountFromLinkData(data: Record<string, any>): {
  type: string;
  data: Record<string, any>;
} {
  return {
    type: SHAREABLE.from_link,
    data,
  };
}

export function setPageState(namespace: string, data: any, replace?: boolean): ShareableActionPayload {
  return {
    type: SHAREABLE.set,
    namespace,
    data,
  };
}

const GetBookmarkedReports = new ReduxAsyncAction('SHAREABLE_GET_BOOKMARKS');
GetBookmarkedReports.registerRequest(() => {
  return dash_api.get(`${BOOKMARK_PATH}/reports`).then((res) => {
    return {
      bookmarks: res.data,
    };
  });
});
export const GET_BOOKMARKS = GetBookmarkedReports.constants;
export const getBookmarkedReports = GetBookmarkedReports.request;
const SaveBookmarkedReport = new ReduxAsyncAction('SHAREABLE_SAVE_BOOKMARK');
SaveBookmarkedReport.registerRequest((payload) => {
  return dash_api.post(`${BOOKMARK_PATH}/save`, payload).then((res) => {
    return {
      bookmark: res.data,
    };
  });
});
export const SAVE_BOOKMARK = SaveBookmarkedReport.constants;
export const saveBookmarkedReport = SaveBookmarkedReport.request;

export function resetState(namespace: string): {
  type: string;
  namespace: string;
} {
  return {
    type: SHAREABLE.reset,
    namespace,
  };
}

const UpdateBookmarkedReport = new ReduxAsyncAction('SHAREABLE_UPDATE_BOOKMARK');
UpdateBookmarkedReport.registerRequest((bookmark, updatedBookmark) => {
  return dash_api
    .post(`${BOOKMARK_PATH}/update`, {
      bookmark,
      updatedBookmark,
    })
    .then((res) => {
      return {
        previousBookmark: bookmark,
        updatedBookmark: res.data,
      };
    });
});
export const UPDATE_BOOKMARK = UpdateBookmarkedReport.constants;
export const updateBookmarkedReport = UpdateBookmarkedReport.request;
const DeleteBookmarkedReport = new ReduxAsyncAction('SHAREABLE_DELETE_BOOKMARK');
DeleteBookmarkedReport.registerRequest((bookmark) => {
  return dash_api.post(`${BOOKMARK_PATH}/delete`, bookmark).then(() => {
    return {
      deletedBookmark: bookmark,
    };
  });
});
export const DELETE_BOOKMARK = DeleteBookmarkedReport.constants;
export const deleteBookmarkedReport = DeleteBookmarkedReport.request;
let previousLinkData;

export function createShareableLink(
  namespace: string,
  data: Record<string, any>
): (dispatch: (action: Record<string, any>) => any) => any {
  return (dispatch) => {
    if (R.equals(data, previousLinkData)) {
      return B.resolve();
    }

    dispatch({
      type: SHAREABLE.link_request,
    });
    const url = removeQueryParam(generateDashDomain(window.location), '_branch_match_id');
    return new B((resolve, reject) =>
      window.branch.link(
        {
          data: {
            $fallback_url: url,
            $canonical_url: url,
            shareablePageState: {
              namespace: namespace,
              app_id: getAppId(window),
              organization_id: getOrganizationId(window),
              data: data,
            },
          },
        },
        (err, link) => {
          if (err) {
            dispatch({
              type: SHAREABLE.link_error,
            });
            reject(err);
          }

          dispatch({
            type: SHAREABLE.link_response,
            data: link,
          });
          previousLinkData = data;
          resolve(link);
        }
      )
    );
  };
}
